<template>
  <div class="container">
    <div class="subheader">
      <template v-if="initialized">
        <div class="title" v-if="questionId">
          <router-link
            :to="{
              name: 'questions',
              params: { sectionId: sectionId, questionId: question.id },
            }"
            class="link"
          >
            #V{{ question.id }}
          </router-link>
          - {{ questionLanguageText() }}
        </div>
        <div class="title" v-if="!questionId">Nieuwe vraag</div>
        <div class="buttons" v-if="enabled">
          <button
            type="button"
            class="btn btn-invisible-grey"
            @click="deleteQuestion"
            v-if="questionId > 0"
            :disabled="isSaving"
          >
            Verwijderen
          </button>
          <button
            type="button"
            class="btn btn-dark"
            @click="saveQuestion()"
            :disabled="!changed || isSaving"
          >
            <font-awesome-icon v-if="isSaving" icon="spinner" />
            Opslaan
          </button>
        </div>
      </template>
    </div>

    <div class="scroller">
      <div class="center" v-if="!initialized && questionId">
        <font-awesome-icon icon="spinner" size="2x" />
      </div>
      <div class="question" v-if="initialized">
        <div class="field">
          <label>Sectie</label>
          <select v-model="question.section" :disabled="!enabled">
            <option
              v-for="section in sections"
              :key="section.id"
              :value="section"
            >
              {{ languageFallback(section, 'title') }}
            </option>
          </select>
        </div>

        <div class="field">
          <label>Code</label>
          <input
            type="text"
            v-model="question.code"
            :disabled="!enabled"
            class="uppercase"
          />
        </div>

        <MultiLanguageInputField
          :language="language"
          :inputType="'textarea'"
          class="field"
          :label="'Vraag'"
          :model="question"
          :valueKey="'text'"
          :enabled="enabled"
        />
        <MultiLanguageInputField
          :language="language"
          :inputType="'textarea'"
          :label="'Uitleg patient'"
          class="field"
          :model="question"
          :valueKey="'explanation'"
          :enabled="enabled"
        />

        <div class="field">
          <label>Notities</label>
          <textarea v-model="question.notes" :disabled="!enabled"></textarea>
        </div>

        <div class="field">
          <label>Conditie</label>
          <select
            @change="question.condition_option_id = null"
            v-model="conditionQuestion1"
            :disabled="!enabled"
          >
            <option :value="null">-</option>
            <option
              v-for="q in conditionQuestions"
              :key="q.id"
              :value="q"
              :title="languageFallback(q, 'text')"
            >
              #V{{ q.id }} - {{ truncate(languageFallback(q, 'text')) }}
            </option>
          </select>

          <select
            v-model="question.condition_option_id"
            v-if="conditionQuestion1"
            :disabled="!enabled"
          >
            <option
              v-for="option in conditionQuestion1.options"
              :key="option.id"
              :value="option.id"
              :title="languageFallback(option, 'text')"
            >
              {{ truncate(languageFallback(option, 'text')) }}
            </option>
          </select>
        </div>
        <div
          v-if="
            question.condition_option_id !== null ||
            question.condition_option_2_id !== null
          "
        >
          <div class="field">
            <label>En / Of</label>
            <select
              v-if="conditionOperatorSelected"
              v-model="question.condition_operator"
              :disabled="!enabled"
            >
              <option
                v-for="option in operatorOptions"
                :value="option.value"
                :key="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div
            class="field"
            v-if="
              question.condition_option_id !== null ||
              question.condition_option_2_id !== null
            "
          >
            <label>Deze conditie</label>
            <select
              @change="question.condition_option_2_id = null"
              v-model="conditionQuestion2"
              :disabled="!enabled"
            >
              <option :value="null">-</option>
              <option
                v-for="q in conditionQuestions"
                :key="q.id"
                :value="q"
                :title="languageFallback(q, 'text')"
              >
                #V{{ q.id }} - {{ truncate(languageFallback(q, 'text')) }}
              </option>
            </select>
            <select
              v-model="question.condition_option_2_id"
              v-if="conditionQuestion2"
              :disabled="!enabled"
            >
              <option
                v-for="option in conditionQuestion2.options"
                :key="option.id"
                :value="option.id"
                :title="languageFallback(option, 'text')"
              >
                {{ truncate(languageFallback(option, 'text')) }}
              </option>
            </select>
          </div>
        </div>

        <div class="field select">
          <label>Speciale conditie</label>
          <v-select
            class="select-field"
            v-model="question.special_condition"
            :options="specialConditions"
            label="label"
            :reduce="(option) => option.value"
            :multiple="false"
            :searchable="true"
            :disabled="!enabled"
            @input="specialConditionChanged"
          />
        </div>

        <div class="field" v-if="question.special_condition">
          <label>En / Of</label>
          <select
            v-if="conditionOperatorSelected"
            v-model="question.special_condition_operator"
            :disabled="!enabled"
            @change="specialConditionOperatorSet"
          >
            <option :value="null">-</option>
            <option
              v-for="option in operatorOptions"
              :value="option.value"
              :key="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="field select" v-if="question.special_condition_operator">
          <label>Speciale conditie</label>
          <v-select
            class="select-field"
            v-model="question.special_condition_2"
            :options="specialConditions"
            label="label"
            :reduce="(option) => option.value"
            :multiple="false"
            :searchable="true"
            :disabled="!enabled"
          />
        </div>

        <div class="field positive-tags">
          <label>Tags inclusief</label>
          <v-select
            class="select-field"
            :options="positiveTagsAvailable"
            label="label"
            :reduce="(tag) => tag.value"
            :multiple="true"
            :searchable="true"
            :disabled="!enabled"
            v-model="question.positive_tags"
          />
        </div>

        <div class="field negative-tags">
          <label>Tags exclusief</label>
          <v-select
            class="select-field"
            :options="negativeTagsAvailable"
            label="label"
            :reduce="(tag) => tag.value"
            :multiple="true"
            :searchable="true"
            :disabled="!enabled"
            v-model="question.negative_tags"
          />
        </div>

        <div class="field">
          <label>Speciaal type</label>
          <v-select
            class="select-field"
            :options="specialTypes"
            v-model="question.special_type"
            label="label"
            :reduce="(type) => type.value"
            :multiple="false"
            :disabled="!enabled"
            @input="specialTypeSet"
          />
        </div>

        <div class="field" v-if="!question.special_type">
          <label>Type</label>
          <select v-model="question.type" :disabled="!enabled">
            <option v-for="type in types" :key="type.id" :value="type.label">
              {{ type.description }}
            </option>
          </select>
        </div>

        <div class="field" v-if="showOptionInputs">
          <label>Keuzes</label>

          <draggable
            class="options"
            handle=".draghandle"
            :forceFallback="true"
            element="div"
            v-model="question.options"
            animation="200"
            :disabled="disableOptionEditing"
          >
            <div
              class="option"
              v-for="option in question.options"
              :key="option.id"
            >
              <font-awesome-icon
                :icon="['fas', 'arrows-alt']"
                class="draghandle"
                v-if="!disableOptionEditing"
              />
              <MultiLanguageInputField
                :enabled="!disableOptionEditing"
                :language="language"
                inputType="input"
                :model="option"
                @afterInputChanged="changeOption"
                class="option-field"
                valueKey="text"
              />

              <select
                v-if="question.special_type === 'bloodthinner-options'"
                v-model="option.type"
                :disabled="disableOptionEditing"
              >
                <option
                  v-for="type in bloodthinnerOptions"
                  :key="type.value"
                  :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select>

              <!-- Note: the code can be edited even if option text editing is disabled,
                   as long as editing isn't disabled altogether (i.e. the version is open) -->
              <input
                v-model="option.code"
                placeholder="Code"
                class="uppercase"
                :disabled="!enabled"
              />

              <button
                @click.prevent="deleteOption(option)"
                v-if="!disableOptionEditing && (option.text || option.id)"
                class="btn btn-invisible"
                title="Verwijderen"
              >
                <font-awesome-icon :icon="['fas', 'trash-alt']" />
              </button>
            </div>
          </draggable>
        </div>

        <div class="divider" v-if="question.related_rules.length > 0"></div>

        <div class="field" v-if="question.related_rules.length > 0">
          <label>Gebruikt in regels</label>
          <div class="relateds">
            <div
              v-for="relatedRule in question.related_rules"
              :key="relatedRule.id"
              class="related"
            >
              <router-link
                :to="{
                  name: 'rules',
                  params: {
                    versionId: version.id,
                    sectionId: relatedRule.section.id,
                    ruleId: relatedRule.id,
                  },
                }"
              >
                #R{{ relatedRule.id }} - {{ relatedRule.label }}
              </router-link>
              <span class="sectionref"> - {{ relatedRule.section.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// This component name is a preexisting condition, disable inspection.
/* eslint vue/multi-word-component-names: 0 */
import api, {
  deleteQuestion,
  getQuestion,
  getQuestionSections,
  getQuestionsSection,
  getSpecialConditions,
  getSpecialQuestionTypes,
  insertQuestion,
  updateQuestion,
} from '@/api';
import { cloneDeep, difference, isEqual } from 'lodash';
import types from '@/config/questionTypes';
import languages, { getValue, supportedLanguages } from '@/languages';
import MultiLanguageInputField from './MultiLanguageInputField';
import promiseStore from '../promiseStore';
import bloodthinnerOptions from '../config/bloodthinnerOptions';
import { whileSaving } from '@/util/tools';

function typeHasOptions(type) {
  return ['select', 'multiselect', 'score'].includes(type);
}

export default {
  props: ['sectionId', 'questionId', 'version', 'language'],
  components: { MultiLanguageInputField },
  data() {
    return {
      types,
      specialTypes: [],
      specialConditions: [],
      questions: null,
      question: null,
      sections: null,
      section: null,
      originalQuestion: null,
      conditionQuestion1: null,
      conditionQuestion2: null,
      tagsAvailable: [],
      bloodthinnerOptions: bloodthinnerOptions,
      conditionOperatorSelected: null,
      operatorOptions: [
        {
          value: 'or',
          label: 'OF',
        },
        {
          value: 'and',
          label: 'EN',
        },
      ],
      isSaving: false,
    };
  },
  beforeRouteLeave(to, from, next) {
    this.guard(next);
  },
  beforeRouteUpdate(to, from, next) {
    this.guard(next);
  },
  created() {
    this.load();
  },
  computed: {
    positiveTagsAvailable() {
      const tags = this.tagsAvailable.filter((tag) => {
        return !(this.question.negative_tags || []).find(
          (tagValue) => tagValue === tag.value
        );
      });

      const missingTags = difference(
        this.question.positive_tags,
        tags.map((v) => v.value)
      ).map((tag) => {
        return { name: 'Missing:' + tag, value: tag };
      });

      return [...tags, ...missingTags];
    },
    negativeTagsAvailable() {
      const tags = this.tagsAvailable.filter((tag) => {
        return !(this.question.positive_tags || []).find(
          (tagValue) => tagValue === tag.value
        );
      });

      const missingTags = difference(
        this.question.negative_tags,
        tags.map((v) => v.value)
      ).map((tag) => {
        return { name: 'Missing:' + tag, value: tag };
      });

      return [...tags, ...missingTags];
    },
    enabled() {
      return !!this.version.open;
    },
    initialized() {
      return (
        this.questions !== null &&
        this.question !== null &&
        this.sections !== null &&
        this.section !== null
      );
    },
    conditionQuestions() {
      return this.questions
        .filter(this.canHaveOptions)
        .filter((q) => q.id !== this.question.id);
    },
    changed() {
      return !isEqual(this.originalQuestion, this.question);
    },
    hasQuestionId() {
      return (
        this.questionId || this.questionId === 0 || this.questionId === '0'
      );
    },
    exists() {
      return this.questionId > 0;
    },
    showOptionInputs() {
      return typeHasOptions(this.question.type);
    },

    // Whether option editing should be enabled. We previously disabled
    // option editing for special question types. This has a few problems:
    // - We have to make an exception for editing the option code
    // - Adding / updating a translation is no longer possible
    // - If the special type options change for whatever reason, we cannot
    //   modify them in place, we can only create a new set of options
    //   from the special type prototype. This may break rule references
    //   to these options (or rather just prevent us from saving them).
    // We choose instead to have option editing always enabled, so at
    // least it is clear what is happening.
    disableOptionEditing() {
      return !this.enabled;
    },
  },

  methods: {
    specialTypeSet(type) {
      const obj = this.findSpecialType(type);
      if (obj?.type) {
        this.question.type = obj.type;
      }
      if (obj?.options?.length) {
        this.question.options = obj.options.map((option) =>
          this.getNewOption(option.code, option.text)
        );

        // We still insert an empty option even if the options
        // are "fixed", in case prefilled options and actual
        // options get out of sync.
        this.appendOptionRowIfNeeded();
      }
    },

    // Clear the second special condition if the operator is cleared.
    specialConditionOperatorSet(event) {
      if (!event?.target?.value) {
        this.question.special_condition_2 = null;
      }
    },
    specialConditionChanged() {
      // If the first special condition is cleared while the second is
      // still present, move the second to the first.
      if (
        !this.question.special_condition &&
        this.question.special_condition_2
      ) {
        this.question.special_condition = this.question.special_condition_2;
        this.question.special_condition_2 = null;
        this.question.special_condition_operator = null;
      }
    },
    languageFallback(item, key) {
      return getValue(supportedLanguages, this.language, item[key]);
    },
    questionLanguageText() {
      return getValue(
        supportedLanguages,
        this.language,
        this.originalQuestion['text']
      );
    },
    guard(next) {
      if (this.changed) {
        this.$emit('popup', {
          title: 'Weet je zeker dat je wilt weggaan?',
          content: 'De wijzigingen in de vraag zullen niet opgeslagen worden',
          image: 'question',
          confirm: () => {
            this.$emit('popup', null);
            next();
          },
          cancel: () => {
            this.$emit('popup', null);
            next(false);
          },
        });
      } else {
        next();
      }
    },
    canHaveOptions(question) {
      return ['select', 'multiselect', 'score'].includes(question.type);
    },
    initTagsAvailable() {
      const questionTagsPromise = promiseStore.getOrSet('tags-available', () =>
        api.getTagsAvailable().then((response) => response.data)
      );

      questionTagsPromise.then((tags) => {
        this.tagsAvailable = tags;
      });
    },
    async initSpecialConditions() {
      const response = await promiseStore.getOrSet('special-conditions', () =>
        getSpecialConditions()
      );

      this.specialConditions = response.data;
    },
    async initSpecialQuestionTypes() {
      const response = await promiseStore.getOrSet(
        'special-question-types',
        () => getSpecialQuestionTypes()
      );
      this.specialTypes = response.data;
    },
    async load() {
      this.initTagsAvailable();
      await Promise.all([
        this.initSpecialConditions(),
        this.initSpecialQuestionTypes(),
      ]);

      if (!this.hasQuestionId) {
        return;
      }

      this.sections = (await getQuestionSections(this.version)).data;
      this.section = (
        await getQuestionsSection(this.version, this.sectionId)
      ).data;

      this.questions = this.section.questions;
      const question =
        this.questionId > 0
          ? (await getQuestion(this.version, this.questionId)).data
          : this.getNewQuestion();

      this.setQuestion(question);
    },
    setQuestion(question) {
      this.question = question;
      this.question.section = this.sections.find(
        (s) => s.id === this.question.section.id
      );

      // Append an extra option row so a new option can be
      // added easily. Do this before storing the question clone
      // storing so it doesn't show up as a change.
      this.appendOptionRowIfNeeded();
      this.originalQuestion = cloneDeep(this.question);

      this.fixProperties();

      this.conditionQuestion1 = this.findQuestionByOptionId(
        this.question.condition_option_id
      );
      this.conditionQuestion2 = this.findQuestionByOptionId(
        this.question.condition_option_2_id
      );
      this.conditionOperatorSelected = this.operatorOptions.find(
        (v) => v.value === question.condition_operator
      );
    },

    // Fixes some question properties that might have become inconsistent.
    // Ignored for closed versions.
    fixProperties() {
      if (!this.version.open) {
        return;
      }

      if (this.question.special_type) {
        const typeData = this.findSpecialType(this.question.special_type);

        if (typeData) {
          // Make sure the question type is synchronized with
          // the registered question type. Mismatches here have caused
          // issues in the past. We make the change explicit (after
          // the change checking clone was created) so a mismatch
          // is noticed.
          this.question.type = typeData.type;
        }

        // Option codes were introduced later, attempt to sync
        // these codes to the corresponding questions. We cannot
        // simply override the options, because we would lose option IDs.
        // This change is also explicit.
        this.syncOptionCodes(typeData);
      }

      if (!typeHasOptions(this.question.type)) {
        // Explicitly clear the options except for the empty row.
        this.question.options = [];
        this.appendOptionRowIfNeeded();
      }
    },
    findSpecialType(typeStr) {
      return this.specialTypes.find((t) => t.value === typeStr);
    },
    syncOptionCodes(typeData) {
      const typeOptions = typeData?.options ?? [];

      if (typeData && !typeData.options_editable && !typeOptions.length) {
        // Question that accidentally has options even though it shouldn't.
        // Remove the options.
        this.question.options = [];

        // We append the empty option row because it was probably there
        // when the question was created, and we don't want the question
        // to seem modified because of just that.
        this.appendOptionRowIfNeeded();
        return;
      }

      if (!typeOptions.length) {
        return;
      }

      for (const option of this.question.options) {
        const text = option.text;

        if (!Object.keys(option.text).some((lang) => option.text[lang])) {
          // This is an empty option, don't match to anything.
          continue;
        }

        // Match an option if all language keys match. A missing value on the current option
        // matches anything.
        const match = typeOptions.find((typeOption) =>
          Object.keys(typeOption.text).every(
            (lang) => !text[lang] || text[lang] === typeOption.text[lang]
          )
        );

        if (match) {
          option.code = match.code;
          option.text = cloneDeep(match.text);
        }
      }
    },

    getNewQuestion() {
      return {
        section: this.section,
        code: '',
        type: 'select',
        text: languages.newTranslationObject('?'),
        position: 1000000,
        is_questionnaire_replacement: 0,
        options: [
          this.getNewOption('YES', languages.newTranslationObject('yes')),
          this.getNewOption('NO', languages.newTranslationObject('no')),
        ],
        notes: '',
        explanation: languages.newTranslationObject(null),
        condition_option_id: null,
        condition_option_2_id: null,
        condition_operator: this.operatorOptions[0].value,
        related_rules: [],
        special_type: null,
        special_condition_operator: null,
        special_condition: null,
        special_condition_2: null,
      };
    },

    /**
     * Create a new 'option' for multiple choice questions.
     */
    getNewOption(code = null, languageValues = null) {
      return {
        id: null,
        code,
        text: languageValues
          ? languageValues
          : languages.newTranslationObject(null),
      };
    },
    truncate(value, length = 45) {
      return value && value.length > length
        ? value.substring(0, length) + '...'
        : value;
    },
    findQuestionByOptionId: function (optionId) {
      return this.questions.find((q) => this.questionHasOption(q, optionId));
    },
    questionHasOption(question, optionId) {
      return (
        question.options && question.options.find((o) => o.id === optionId)
      );
    },
    async saveQuestion() {
      const payload = cloneDeep(this.question);

      // All caps is faked with CSS, make sure we actually upper case here.
      payload.code = payload.code ? payload.code.toUpperCase() : null;

      if (typeHasOptions(payload.type)) {
        payload.options = payload.options
          .filter((o) => o.text && supportedLanguages.some((l) => o.text[l]))
          .map((o) => {
            o.code = o.code ? o.code.toUpperCase() : null;
            return o;
          });
      } else {
        payload.options = null;
      }

      if (payload.section.id !== parseInt(this.sectionId)) {
        payload.condition_option_id = null;
        payload.condition_option_2_id = null;
        payload.condition_operator = this.operatorOptions[0].value;
        payload.position = 0;
      } else {
        payload.condition_option_id = payload.condition_option_id || null;
        payload.condition_option_2_id = payload.condition_option_2_id || null;
        payload.condition_operator = payload.condition_operator || null;
      }

      try {
        await whileSaving(this, 'isSaving', async () => {
          if (this.exists) {
            const response = await updateQuestion(this.version, payload);
            this.setQuestion(response.data);
            if (this.question.section.id !== parseInt(this.sectionId)) {
              this.pushQuestion(this.question);
            } else {
              this.$emit('refreshMiddle');
            }
          } else {
            const response = await insertQuestion(this.version, payload);
            this.setQuestion(response.data);
            this.$emit('refreshMiddle');
            this.pushQuestion(this.question);
          }
        });
      } catch (error) {
        this.$emit('popup', {
          title: 'Kan wijzingen niet opslaan.',
          image: 'error',
          error: error.response.data.message,
          confirm: () => {
            this.$emit('popup', null);
          },
        });
      }
    },
    pushQuestion(question) {
      this.$router.push({
        name: 'questions',
        params: {
          versionId: this.version.id,
          sectionId: question.section.id,
          questionId: question.id,
        },
      });
    },
    deleteQuestion() {
      this.$emit('popup', {
        title: 'Vraag verwijderen?',
        content: 'Dit kan niet ongedaan gemaakt worden.',
        image: 'question',
        confirm: async () => {
          this.$emit('popup', null);
          try {
            await whileSaving(this, 'isSaving', () =>
              deleteQuestion(this.version, this.question)
            );

            this.$router.push({
              name: 'questions',
              params: {
                versionId: this.version.id,
                sectionId: this.question.section.id,
              },
            });
            this.$emit('refreshMiddle');
          } catch (error) {
            this.$emit('popup', {
              title: 'Kan vraag niet verwijderen',
              image: 'error',
              error: error.response.data.message,
              confirm: () => {
                this.$emit('popup', null);
              },
            });
          }
        },
        cancel: () => this.$emit('popup', null),
      });
    },
    deleteOption(option) {
      this.question.options.splice(this.question.options.indexOf(option), 1);
      this.appendOptionRowIfNeeded();
    },
    changeOption() {
      this.appendOptionRowIfNeeded();
    },
    appendOptionRowIfNeeded() {
      if (!this.version.open) {
        return;
      }

      const optionCount = this.question.options.length;

      // Add a new row if no options are there OR if the last option has text.
      if (optionCount === 0 || this.question.options[optionCount - 1].text.nl) {
        this.question.options.push(this.getNewOption());
      }
    },
  },
};
</script>

<style scoped lang="scss">
:deep(.field) {
  display: flex;
  flex-direction: row;
  padding: 6px 0;

  label {
    width: 120px;
    text-align: right;
    color: grey;
    padding: 6px 14px 0 0;
    font-size: 14px;
    flex-grow: 0;
    flex-shrink: 0;
  }

  // Exclude the vue-select hidden input
  input:not(.vs__selected-options input),
  textarea,
  select {
    border-radius: 3px;
    border: 1px solid lightgrey;
    font-size: 14px;
    padding: 4px 6px;
    min-height: 24px;
    margin: 0 10px 0 0;
    outline-style: none;
  }

  .select-field {
    input {
      border: none;
    }
  }

  input {
    width: calc(100% - 240px);
  }

  textarea {
    width: calc(100% - 240px);
    height: 8em;
    line-height: 1.4;
  }

  textarea:disabled,
  input:disabled,
  select:disabled {
    background-color: $bg-color-disabled;
  }

  .options {
    .option {
      display: flex;
      flex-direction: row;
      align-items: center;
      height: 3em;

      .draghandle {
        color: black;
        margin: 8px 2px 0 8px;
        cursor: grab;
      }

      .option-field {
        input {
          width: 400px;
          margin: 0 4px 0 8px;
        }

        button {
          margin: 0;
        }
      }
    }
  }
}

.container {
  display: flex;
  flex-direction: column;

  .select {
    border: none;
  }

  .scroller {
    overflow-y: auto;

    .question {
      padding-top: 20px;
      padding-bottom: 60px;
      font-family: $font-family-gitlab;
      display: flex;
      flex-direction: column;

      .divider {
        margin: 30px 0 20px 0;
        width: 100%;
        height: 1px;
        background-color: #e4e4e4;
      }
    }
  }
}

.uppercase {
  text-transform: uppercase;
}
</style>
